import { useCallback, useEffect, useRef, useState } from "react";
import type { Conversation, ExecutionTraceStep, Message } from "../data/types";
import { MESSAGE_ENTER_MS } from "./messageMotion";
import {
  TRACE_LINE_GAP_MS,
  TRACE_SETTLE_MS,
  TRACE_STEP_PAUSE_MS,
  countStepLines,
  estimateStepLineStreamMs,
} from "./streamText";

const PAUSE_RANGE = {
  first: [380, 560],
  user: [800, 1500],
  agent: [1500, 2500],
} as const;

function randomInRange([min, max]: readonly [number, number]) {
  return Math.round(min + Math.random() * (max - min));
}

function gapBeforeMessage(previous: Message | undefined, current: Message) {
  if (!previous) return randomInRange(PAUSE_RANGE.first);
  if (current.sender === "ai") return randomInRange(PAUSE_RANGE.agent);
  return randomInRange(PAUSE_RANGE.user);
}

export type ExecutionTraceViewItem =
  | {
      type: "step";
      id: string;
      step: ExecutionTraceStep;
      visibleLineCount: number;
    }
  | {
      type: "separator";
      id: string;
      timestamp: string;
    };

function buildFullTraceItems(
  conversation?: Conversation,
): ExecutionTraceViewItem[] {
  if (!conversation) return [];
  const items: ExecutionTraceViewItem[] = [];

  conversation.messages.forEach((message) => {
    if (message.sender !== "ai" || !message.executionTrace?.length) return;

    message.executionTrace.forEach((step, index) => {
      items.push({
        type: "step",
        id: `${message.id}-step-${index}`,
        step,
        visibleLineCount: countStepLines(step),
      });
    });

    items.push({
      type: "separator",
      id: `${message.id}-separator`,
      timestamp: message.timestamp ?? "",
    });
  });

  return items;
}

export type InboxDemoExecutionState = {
  messageId: string;
  items: ExecutionTraceViewItem[];
  isRunning: boolean;
};

const EMPTY_EXECUTION: InboxDemoExecutionState = {
  messageId: "",
  items: [],
  isRunning: false,
};

type UseInboxDemoPlaybackArgs = {
  enabled: boolean;
  conversation?: Conversation;
  selectedConversationId: string;
  reducedMotion: boolean;
};

export function useInboxDemoPlayback({
  enabled,
  conversation,
  selectedConversationId,
  reducedMotion,
}: UseInboxDemoPlaybackArgs) {
  const conversationRef = useRef(conversation);
  conversationRef.current = conversation;

  const [revealedCount, setRevealedCount] = useState(() =>
    enabled && !reducedMotion ? 0 : conversation?.messages.length ?? 0,
  );
  const [isPlaying, setIsPlaying] = useState(() => enabled && !reducedMotion);
  const [execution, setExecution] =
    useState<InboxDemoExecutionState>(EMPTY_EXECUTION);

  const timersRef = useRef<number[]>([]);
  const cancelledRef = useRef(false);
  const finishedRef = useRef(false);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];
  }, []);

  const wait = useCallback((ms: number) => {
    return new Promise<void>((resolve) => {
      const id = window.setTimeout(resolve, ms);
      timersRef.current.push(id);
    });
  }, []);

  const stop = useCallback(() => {
    cancelledRef.current = true;
    finishedRef.current = true;
    clearTimers();
    setIsPlaying(false);
    setRevealedCount(conversationRef.current?.messages.length ?? 0);
    setExecution({
      messageId: "",
      items: buildFullTraceItems(conversationRef.current),
      isRunning: false,
    });
  }, [clearTimers]);

  useEffect(() => {
    const target = conversationRef.current;

    if (!enabled || !target || reducedMotion) {
      setIsPlaying(false);
      setRevealedCount(target?.messages.length ?? 0);
      setExecution({
        messageId: "",
        items: buildFullTraceItems(target),
        isRunning: false,
      });
      return;
    }

    if (selectedConversationId !== target.id) {
      stop();
      return;
    }

    if (finishedRef.current) return;

    cancelledRef.current = false;
    setIsPlaying(true);
    setRevealedCount(0);
    setExecution(EMPTY_EXECUTION);

    const messages = target.messages;
    let items: ExecutionTraceViewItem[] = [];

    const run = async () => {
      for (let index = 0; index < messages.length; index += 1) {
        if (cancelledRef.current) return;

        const current = messages[index];
        const previous = messages[index - 1];
        const trace =
          current.sender === "ai" ? current.executionTrace : undefined;

        if (previous) {
          await wait(MESSAGE_ENTER_MS);
          if (cancelledRef.current) return;
        }

        if (trace && trace.length > 0) {
          setExecution({
            messageId: current.id,
            items,
            isRunning: true,
          });

          for (let stepIndex = 0; stepIndex < trace.length; stepIndex += 1) {
            const step = trace[stepIndex];
            const lineCount = countStepLines(step);
            const stepId = `${current.id}-step-${stepIndex}`;

            await wait(randomInRange(TRACE_STEP_PAUSE_MS));
            if (cancelledRef.current) return;

            items = [
              ...items,
              {
                type: "step",
                id: stepId,
                step,
                visibleLineCount: 1,
              },
            ];
            setExecution({
              messageId: current.id,
              items,
              isRunning: true,
            });

            for (let lineIndex = 1; lineIndex < lineCount; lineIndex += 1) {
              await wait(
                estimateStepLineStreamMs(step, lineIndex - 1) + TRACE_LINE_GAP_MS,
              );
              if (cancelledRef.current) return;

              items = items.map((item) =>
                item.type === "step" && item.id === stepId
                  ? { ...item, visibleLineCount: lineIndex + 1 }
                  : item,
              );
              setExecution({
                messageId: current.id,
                items,
                isRunning: true,
              });
            }
          }

          await wait(TRACE_SETTLE_MS);
          if (cancelledRef.current) return;

          items = [
            ...items,
            {
              type: "separator",
              id: `${current.id}-separator`,
              timestamp: current.timestamp ?? "",
            },
          ];
          setExecution({
            messageId: current.id,
            items,
            isRunning: false,
          });
        } else {
          const pause = gapBeforeMessage(previous, current);
          if (pause > 0) await wait(pause);
          if (cancelledRef.current) return;
        }

        setRevealedCount(index + 1);
      }

      if (!cancelledRef.current) {
        finishedRef.current = true;
        setIsPlaying(false);
      }
    };

    void run();

    return () => {
      cancelledRef.current = true;
      clearTimers();
    };
  }, [
    clearTimers,
    conversation?.id,
    enabled,
    reducedMotion,
    selectedConversationId,
    stop,
    wait,
  ]);

  return {
    revealedCount,
    isPlaying,
    execution,
    stop,
  };
}
